import { type FC } from 'react';
import { useFragment, graphql, usePaginationFragment } from 'react-relay';

import { triggerConstants } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes';

import { HpSharedItemTilesCarouselModule } from '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarouselModule';

import { type HpSharedFeaturedModuleCarouselVariant_viewer$key } from './__generated__/HpSharedFeaturedModuleCarouselVariant_viewer.graphql';
import { type HpSharedFeaturedModuleCarouselVariant_user$key } from './__generated__/HpSharedFeaturedModuleCarouselVariant_user.graphql';

const viewerFragment = graphql`
    fragment HpSharedFeaturedModuleCarouselVariant_viewer on Viewer
    @refetchable(queryName: "HpSharedFeaturedModuleCarouselVariantPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        onSaleItems: itemSearchTrending(first: $count, after: $cursor, module: ON_SALE_MODULE)
            @connection(key: "HpSharedFeaturedModuleCarouselVariant_onSaleItems", filters: []) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    ...HpSharedItemTilesCarouselModule_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarouselModule_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModuleCarouselVariant_user on User {
        ...HpSharedItemTilesCarouselModule_user
    }
`;

export const HpSharedFeaturedModuleCarouselVariant: FC<{
    viewer?: HpSharedFeaturedModuleCarouselVariant_viewer$key | null;
    user?: HpSharedFeaturedModuleCarouselVariant_user$key | null;
    isMobile: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const { onSaleItems } = viewer || {};
    const items = flattenConnectionNodes(onSaleItems);
    const totalResults = onSaleItems?.totalResults || 0;

    return (
        <HpSharedItemTilesCarouselModule
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            totalResults={totalResults}
            trackingList={triggerConstants.TRIGGER_SALE_MODULE}
            loadNext={loadNext}
            title={
                <FormattedMessage
                    id="HpSharedFeaturedModuleCarouselVariant.title"
                    defaultMessage="Today’s Top Deals"
                />
            }
            viewMoreLink="/sale/saturday-sale/?sort=newest"
            viewMoreText={
                !isMobile ? (
                    <FormattedMessage
                        id="HpSharedFeaturedModuleCarouselVariant.viewMore"
                        defaultMessage="Shop All Sale"
                    />
                ) : undefined
            }
            dataTn="sale"
        />
    );
};
